import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useEnhancedMobile } from '@/hooks/use-enhanced-mobile';
import { useNavigate } from 'react-router-dom';
import { getPlatformIcon } from '@/components/marketplace/PlatformIcons';
import { useBrandAthletes } from '@/hooks/useBrandAthletes';
import { useESPNAthletes, ESPNAthlete } from '@/hooks/useESPNAthletes';
import { useCreators, Creator } from '@/hooks/useCreators';
import { useSyncedCreators } from '@/hooks/useSyncedCreators';
import { useGroupedCreators, CreatorGroup } from '@/hooks/useGroupedCreators';
import { AthleteEngagementGrid } from '@/components/brand/AthleteEngagementGrid';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SafeAvatarImage } from '@/components/ui/safe-avatar-image';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Search, TrendingUp, TrendingDown, Minus, Users, DollarSign, CheckCircle, Trophy, Shield, User, Ruler, Weight, GraduationCap, Calendar, Users2, Activity, Video, Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from '@/components/ui/pagination';
import { ViewToggle, ViewType } from '@/components/marketplace/ViewToggle';
import { AthleteListView } from '@/components/marketplace/AthleteListView';
import { AthleteCompactView } from '@/components/marketplace/AthleteCompactView';
import { CreatorGroupListView, CreatorListView } from '@/components/marketplace/CreatorListView';
import { CreatorGroupCompactView, CreatorCompactView } from '@/components/marketplace/CreatorCompactView';
import { LoadingFallback } from '@/components/LoadingFallback';
import { EmptyState } from '@/components/EmptyState';
import { useUser } from '@/contexts/UserContext';
import { useDebounce } from '@/hooks/useDebounce';
import { MarketplaceAthleteDetailModal } from '@/components/marketplace/MarketplaceAthleteDetailModal';
import { CreatorDetailModal } from '@/components/marketplace/CreatorDetailModal';
import { GroupedCreatorDetailModal } from '@/components/marketplace/GroupedCreatorDetailModal';

import { useAthleteSignalIntel } from '@/hooks/useAthleteSignalIntel';
import { getProxiedImageUrl } from '@/utils/imageProxy';
import { getLeagueLogo, getLeagueColor } from '@/utils/leagueLogos';
import { getTeamColors } from '@/utils/teamColors';


interface EnhancedTalentMarketplaceProps {
  onSelectForDeal?: (athlete: any) => void;
}

const EnhancedTalentMarketplace: React.FC<EnhancedTalentMarketplaceProps> = ({ onSelectForDeal }) => {
  const navigate = useNavigate();
  const { accountType } = useUser();
  const { isMobile, isTablet } = useEnhancedMobile();
  const isCompact = isMobile || isTablet;
  const { athletes: talents, loading, error, handleViewHistory } = useBrandAthletes();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [filterBySport, setFilterBySport] = useState('all');
  const [activeTab, setActiveTab] = useState('on-platform');
  const [marketplaceFilterBySport, setMarketplaceFilterBySport] = useState('all');
  const [marketplaceFilterByTeam, setMarketplaceFilterByTeam] = useState('all');
  const [marketplaceFilterByClaimStatus, setMarketplaceFilterByClaimStatus] = useState('all');
  const [marketplaceSearchTerm, setMarketplaceSearchTerm] = useState('');
  
  // Pagination state
  const [marketplacePage, setMarketplacePage] = useState(1);
  const [collegiatePage, setCollegiatePage] = useState(1);
  
  // Collegiate athletes state
  const [collegiateFilterBySport, setCollegiateFilterBySport] = useState('all');
  const [collegiateFilterByTeam, setCollegiateFilterByTeam] = useState('all');
  const [collegiateFilterByClaimStatus, setCollegiateFilterByClaimStatus] = useState('all');
  const [collegiateSearchTerm, setCollegiateSearchTerm] = useState('');
  const [selectedCollegiateAthlete, setSelectedCollegiateAthlete] = useState<ESPNAthlete | null>(null);
  const [isCollegiateDetailModalOpen, setIsCollegiateDetailModalOpen] = useState(false);
  const [selectedAthlete, setSelectedAthlete] = useState<ESPNAthlete | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  
  // Creators tab state
  const [creatorsSearchTerm, setCreatorsSearchTerm] = useState('');
  const [creatorsPlatform, setCreatorsPlatform] = useState<'tiktok' | 'instagram' | 'youtube' | 'all'>('all');
  const [selectedCreator, setSelectedCreator] = useState<Creator | null>(null);
  const [selectedCreatorGroup, setSelectedCreatorGroup] = useState<CreatorGroup | null>(null);
  const [isCreatorModalOpen, setIsCreatorModalOpen] = useState(false);
  const [isGroupedCreatorModalOpen, setIsGroupedCreatorModalOpen] = useState(false);
  const [creatorsSearchMode, setCreatorsSearchMode] = useState<'synced' | 'live'>('synced');
  
  // View type state per tab
  const [onPlatformView, setOnPlatformView] = useState<ViewType>('grid');
  const [athletesView, setAthletesView] = useState<ViewType>('grid');
  const [collegiateView, setCollegiateView] = useState<ViewType>('grid');
  const [creatorsView, setCreatorsView] = useState<ViewType>('grid');
  
  
  
  const { getQuickSignal } = useAthleteSignalIntel();
  
  // Debounce creators search
  const debouncedCreatorsSearch = useDebounce(creatorsSearchTerm, 500);
  
  // Fetch grouped creators from database (default view)
  const { data: groupedCreators, isLoading: groupedCreatorsLoading } = useGroupedCreators({
    platform: creatorsPlatform,
    searchTerm: debouncedCreatorsSearch,
  });
  
  // Fetch synced creators from database (for live mode fallback)
  const { data: syncedCreators, isLoading: syncedCreatorsLoading } = useSyncedCreators({
    platform: creatorsPlatform,
    searchTerm: debouncedCreatorsSearch,
  });
  
  // Fetch creators from live API search (only when in live mode and searching)
  const { data: liveCreatorsData, isLoading: liveCreatorsLoading } = useCreators({
    platform: creatorsPlatform === 'all' ? 'tiktok' : creatorsPlatform,
    query: debouncedCreatorsSearch,
    enabled: creatorsSearchMode === 'live' && debouncedCreatorsSearch.length > 0,
  });
  
  // Determine which creators to show
  const displayedCreatorGroups = groupedCreators || [];
  const displayedCreators = creatorsSearchMode === 'synced' 
    ? (syncedCreators || [])
    : (liveCreatorsData?.creators || []);
  const creatorsLoading = creatorsSearchMode === 'synced' ? groupedCreatorsLoading : liveCreatorsLoading;

  // Debounce search term for better performance
  const debouncedMarketplaceSearch = useDebounce(marketplaceSearchTerm, 300);
  const debouncedCollegiateSearch = useDebounce(collegiateSearchTerm, 300);

  const handleMarketplaceSportChange = (sport: string) => {
    setMarketplaceFilterBySport(sport);
    setMarketplaceFilterByTeam('all');
    setMarketplacePage(1);
  };

  const handleCollegiateSportChange = (sport: string) => {
    setCollegiateFilterBySport(sport);
    setCollegiateFilterByTeam('all');
    setCollegiatePage(1);
  };

  // Reset pages when search/filters change
  useEffect(() => { setMarketplacePage(1); }, [debouncedMarketplaceSearch, marketplaceFilterByTeam]);
  useEffect(() => { setCollegiatePage(1); }, [debouncedCollegiateSearch, collegiateFilterByTeam]);

  const handleCollegiateAthleteClick = (athlete: ESPNAthlete) => {
    if (onSelectForDeal) {
      onSelectForDeal(athlete);
      return;
    }
    setSelectedCollegiateAthlete(athlete);
    setIsCollegiateDetailModalOpen(true);
  };

  const handleAthleteClick = (athlete: ESPNAthlete) => {
    if (onSelectForDeal) {
      onSelectForDeal(athlete);
      return;
    }
    setSelectedAthlete(athlete);
    setIsDetailModalOpen(true);
  };

  // Handle inviting athlete to deal - navigate to create-deal page
  const handleInviteAthleteToDeal = (athlete: ESPNAthlete) => {
    navigate('/create-deal', {
      state: {
        prePopulatedDeal: {
          title: `Partnership with ${athlete.display_name || athlete.name}`,
          description: `Sponsorship deal with ${athlete.display_name || athlete.name}, ${athlete.position || ''} for ${athlete.current_team_name || athlete.sport}`,
          deal_type: 'sponsorship',
          target_criteria: `${athlete.sport} - ${athlete.current_team_name || 'Professional Athlete'}`,
        }
      }
    });
  };

  // Handle inviting creator to deal - navigate to create-deal page
  const handleInviteCreatorToDeal = (creator: Creator) => {
    navigate('/create-deal', {
      state: {
        prePopulatedDeal: {
          title: `Partnership with ${creator.display_name}`,
          description: `Collaboration with ${creator.display_name} (@${creator.username}) - ${creator.platform} creator with ${creator.follower_count?.toLocaleString()} followers`,
          deal_type: 'collaboration',
          target_criteria: `${creator.platform} Creator - ${creator.category || 'Content Creator'}`,
        }
      }
    });
  };

  // Handle inviting creator group to deal - navigate to create-deal page
  const handleInviteCreatorGroupToDeal = (creatorGroup: CreatorGroup) => {
    const platformList = creatorGroup.platforms.join(', ');
    navigate('/create-deal', {
      state: {
        prePopulatedDeal: {
          title: `Partnership with ${creatorGroup.display_name || creatorGroup.primary_name}`,
          description: `Multi-platform collaboration with ${creatorGroup.display_name || creatorGroup.primary_name} - ${creatorGroup.total_followers?.toLocaleString()} total followers across ${platformList}`,
          deal_type: 'collaboration',
          target_criteria: `Multi-Platform Creator - ${platformList}`,
        }
      }
    });
  };

  // Creator click handlers that respect onSelectForDeal
  const handleCreatorClick = (creator: Creator) => {
    if (onSelectForDeal) {
      onSelectForDeal(creator);
      return;
    }
    setSelectedCreator(creator);
    setIsCreatorModalOpen(true);
  };

  const handleCreatorGroupClick = (group: CreatorGroup) => {
    if (onSelectForDeal) {
      onSelectForDeal(group);
      return;
    }
    setSelectedCreatorGroup(group);
    setIsGroupedCreatorModalOpen(true);
  };

  // Fetch professional marketplace athletes (pro sports only)
  const proSports = ['nba', 'nfl', 'mlb', 'nhl', 'wnba', 'mls'];
  const PAGE_SIZE = 50;
  const { data: marketplaceAthletesData, isLoading: marketplaceLoading } = useESPNAthletes({
    sport: marketplaceFilterBySport !== 'all' ? marketplaceFilterBySport : undefined,
    searchTerm: debouncedMarketplaceSearch || undefined,
    team: marketplaceFilterByTeam !== 'all' ? marketplaceFilterByTeam : undefined,
    claimStatus: marketplaceFilterByClaimStatus !== 'all' ? marketplaceFilterByClaimStatus : undefined,
    isCollegiate: false,
    enabled: true,
    limit: PAGE_SIZE,
    offset: (marketplacePage - 1) * PAGE_SIZE,
  });

  // Collegiate sports list
  const collegiateSports = ['cfb', 'mcbb', 'wcbb', 'college-baseball', 'college-softball'];
  
  // Fetch collegiate athletes
  const { data: collegiateAthletesData, isLoading: collegiateLoading } = useESPNAthletes({
    sport: collegiateFilterBySport !== 'all' ? collegiateFilterBySport : undefined,
    searchTerm: debouncedCollegiateSearch || undefined,
    team: collegiateFilterByTeam !== 'all' ? collegiateFilterByTeam : undefined,
    claimStatus: collegiateFilterByClaimStatus !== 'all' ? collegiateFilterByClaimStatus : undefined,
    isCollegiate: true,
    enabled: true,
    limit: PAGE_SIZE,
    offset: (collegiatePage - 1) * PAGE_SIZE,
  });

  if (loading) return <LoadingFallback />;
  if (error) return <EmptyState title="Error" description={error} />;

  // Filter and sort on-platform talents
  const onPlatformTalents = talents
    .filter(talent => {
      const matchesSearch = talent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           talent.sport.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesSport = filterBySport === 'all' || talent.sport === filterBySport;
      return matchesSearch && matchesSport;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'payments':
          return b.paymentsDue - a.paymentsDue;
        case 'followers':
          return b.followersCount - a.followersCount;
        case 'campaigns':
          return b.campaignsCount - a.campaignsCount;
        default:
          return a.name.localeCompare(b.name);
      }
    });

  // Calculate on-platform statistics
  const onPlatformStats = {
    total: talents.length,
    avgEngagement: talents.reduce((sum, a) => sum + a.avgEngagementRate, 0) / (talents.length || 1),
    totalPaymentsDue: talents.reduce((sum, a) => sum + a.paymentsDue, 0),
    totalFollowers: talents.reduce((sum, a) => sum + a.followersCount, 0),
  };

  // Marketplace athletes data
  const marketplaceAthletes = marketplaceAthletesData?.athletes || [];
  
  // Filter marketplace athletes
  const filteredMarketplaceAthletes = marketplaceAthletes.filter(athlete => {
    const matchesSearch = !marketplaceSearchTerm || 
      athlete.name.toLowerCase().includes(marketplaceSearchTerm.toLowerCase()) ||
      athlete.sport.toLowerCase().includes(marketplaceSearchTerm.toLowerCase());
    const matchesSport = marketplaceFilterBySport === 'all' || athlete.sport === marketplaceFilterBySport;
    const matchesTeam = marketplaceFilterByTeam === 'all' || athlete.current_team_name === marketplaceFilterByTeam;
    return matchesSearch && matchesSport && matchesTeam;
  });

  // Marketplace statistics (from server-side aggregation)
  const marketplaceStats = {
    total: marketplaceAthletesData?.total || 0,
    verified: marketplaceAthletesData?.stats?.verified ?? marketplaceAthletes.filter(a => a.is_verified).length,
    claimed: marketplaceAthletesData?.stats?.claimed ?? marketplaceAthletes.filter(a => a.claim_status === 'approved').length,
    unclaimed: marketplaceAthletesData?.stats?.unclaimed ?? marketplaceAthletes.filter(a => !a.claim_status || a.claim_status === 'unclaimed').length,
  };

  // Get unique sports and teams for marketplace filter (pro sports only)
  const marketplaceSports = ['nba', 'nfl', 'mlb', 'nhl', 'wnba', 'mls'];
  const marketplaceTeams = [...new Set(marketplaceAthletes.map(a => a.current_team_name).filter(Boolean))].sort() as string[];

  // Collegiate athletes data
  const collegiateAthletes = collegiateAthletesData?.athletes || [];
  
  // Filter collegiate athletes
  const filteredCollegiateAthletes = collegiateAthletes.filter(athlete => {
    const matchesSearch = !collegiateSearchTerm || 
      athlete.name.toLowerCase().includes(collegiateSearchTerm.toLowerCase()) ||
      athlete.sport.toLowerCase().includes(collegiateSearchTerm.toLowerCase());
    const matchesSport = collegiateFilterBySport === 'all' || athlete.sport === collegiateFilterBySport;
    const matchesTeam = collegiateFilterByTeam === 'all' || athlete.current_team_name === collegiateFilterByTeam;
    return matchesSearch && matchesSport && matchesTeam;
  });

  // Collegiate statistics (from server-side aggregation)
  const collegiateStats = {
    total: collegiateAthletesData?.total || 0,
    verified: collegiateAthletesData?.stats?.verified ?? collegiateAthletes.filter(a => a.is_verified).length,
    claimed: collegiateAthletesData?.stats?.claimed ?? collegiateAthletes.filter(a => a.claim_status === 'approved').length,
    unclaimed: collegiateAthletesData?.stats?.unclaimed ?? collegiateAthletes.filter(a => !a.claim_status || a.claim_status === 'unclaimed').length,
  };

  // Get unique teams for collegiate filter
  const collegiateTeams = [...new Set(collegiateAthletes.map(a => a.current_team_name).filter(Boolean))].sort() as string[];

  // Collegiate sports display names
  const collegiateSportsDisplay: Record<string, string> = {
    'cfb': 'College Football',
    'mcbb': "Men's Basketball",
    'wcbb': "Women's Basketball",
    'college-baseball': 'College Baseball',
    'college-softball': 'College Softball',
  };

  // Get unique sports for on-platform filter
  const userSports = [...new Set(talents.map(a => a.sport))];

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex w-full">
          <TabsTrigger value="on-platform" className={`flex items-center gap-1.5 ${isCompact ? 'flex-1 min-w-0 px-2 text-xs' : 'shrink-0 whitespace-nowrap'}`}>
            {!isCompact && <Users className="h-4 w-4" />}
            <span className="truncate">{isCompact ? 'Partnerships' : (accountType === 'brand' ? 'Partnerships' : 'Featured Talent')}</span>
            {!isCompact && <Badge variant="secondary" className="ml-1">{talents.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="athletes" className={`flex items-center gap-1.5 ${isCompact ? 'flex-1 min-w-0 px-2 text-xs' : 'shrink-0 whitespace-nowrap'}`}>
            {!isCompact && <Trophy className="h-4 w-4" />}
            <span className="truncate">{isMobile ? 'Pro' : isTablet ? 'Pro Athletes' : 'Professional Athletes'}</span>
            {!isCompact && <Badge variant="secondary" className="ml-1">{marketplaceStats.total}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="collegiate" className={`flex items-center gap-1.5 ${isCompact ? 'flex-1 min-w-0 px-2 text-xs' : 'shrink-0 whitespace-nowrap'}`}>
            {!isCompact && <GraduationCap className="h-4 w-4" />}
            <span className="truncate">{isMobile ? 'College' : isTablet ? 'College' : 'Collegiate Athletes'}</span>
            {!isCompact && <Badge variant="secondary" className="ml-1">{collegiateStats.total}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="creators" className={`flex items-center gap-1.5 ${isCompact ? 'flex-1 min-w-0 px-2 text-xs' : 'shrink-0 whitespace-nowrap'}`}>
            {!isCompact && <Video className="h-4 w-4" />}
            <span className="truncate">Creators</span>
            {!isCompact && <Badge variant="secondary" className="ml-1">{displayedCreators.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* On Platform Tab */}
        <TabsContent value="on-platform" className="space-y-6">
          {/* On-Platform Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Talent</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{onPlatformStats.total}</div>
                <p className="text-xs text-muted-foreground mt-1">Active on platform</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Payments Due</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">${(onPlatformStats.totalPaymentsDue / 1000).toFixed(0)}K</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Reach</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{(onPlatformStats.totalFollowers / 1000).toFixed(0)}K</div>
              </CardContent>
            </Card>
          </div>

          {/* Filters and Search */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search talent by name or sport..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterBySport} onValueChange={setFilterBySport}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Sports" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sports</SelectItem>
                {userSports.sort().map(sport => (
                  <SelectItem key={sport} value={sport} className="capitalize">
                    {sport}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Talent Score" />
              </SelectTrigger>
                <SelectContent>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="payments">Payments Due</SelectItem>
                <SelectItem value="followers">Followers</SelectItem>
                <SelectItem value="campaigns">Campaigns</SelectItem>
              </SelectContent>
            </Select>
            <ViewToggle view={onPlatformView} onViewChange={setOnPlatformView} />
          </div>

          {/* On-Platform Athletes Grid */}
          {onPlatformTalents.length > 0 ? (
            onPlatformView === 'grid' ? (
              <AthleteEngagementGrid 
                athletes={onPlatformTalents} 
                onViewHistory={handleViewHistory}
              />
            ) : onPlatformView === 'list' ? (
              <AthleteListView
                athletes={onPlatformTalents.map(t => ({
                  id: t.id || t.name,
                  name: t.name,
                  display_name: t.name,
                  sport: t.sport,
                  current_team_name: t.sport,
                  headshot_url: t.avatarUrl,
                }))}
                onAthleteClick={() => {}}
              />
            ) : (
              <AthleteCompactView
                athletes={onPlatformTalents.map(t => ({
                  id: t.id || t.name,
                  name: t.name,
                  display_name: t.name,
                  sport: t.sport,
                  current_team_name: t.sport,
                  headshot_url: t.avatarUrl,
                }))}
                onAthleteClick={() => {}}
              />
            )
          ) : (
            <EmptyState
              title="No On-Platform Talent Found"
              description={searchTerm || filterBySport !== 'all' 
                ? "Try adjusting your search or filter criteria."
                : "No talent has been partnered with yet. Start creating deals to see talent data here."
              }
              icon={<Users className="w-8 h-8 text-muted-foreground" />}
            />
          )}
        </TabsContent>

        {/* Athletes Tab (formerly Marketplace) */}
        <TabsContent value="athletes" className="space-y-6">

          {/* Marketplace Filters and Search */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search athletes by name..."
                value={marketplaceSearchTerm}
                onChange={(e) => setMarketplaceSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={marketplaceFilterBySport} onValueChange={handleMarketplaceSportChange}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Sports" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sports</SelectItem>
                {marketplaceSports.map(sport => (
                  <SelectItem key={sport} value={sport} className="uppercase">
                    {sport.toUpperCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={marketplaceFilterByTeam} onValueChange={setMarketplaceFilterByTeam}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Teams" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Teams</SelectItem>
                {marketplaceTeams.map(team => (
                  <SelectItem key={team} value={team}>
                    {team}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={marketplaceFilterByClaimStatus} onValueChange={setMarketplaceFilterByClaimStatus}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="approved">Claimed</SelectItem>
                <SelectItem value="unclaimed">Unclaimed</SelectItem>
              </SelectContent>
            </Select>
            <ViewToggle view={athletesView} onViewChange={setAthletesView} />
          </div>

          {/* Marketplace Athletes Grid */}
          {marketplaceLoading ? (
            <LoadingFallback />
          ) : filteredMarketplaceAthletes.length > 0 ? (
            athletesView === 'list' ? (
              <AthleteListView athletes={filteredMarketplaceAthletes} onAthleteClick={handleAthleteClick} />
            ) : athletesView === 'compact' ? (
              <AthleteCompactView athletes={filteredMarketplaceAthletes} onAthleteClick={handleAthleteClick} />
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredMarketplaceAthletes.map((athlete: ESPNAthlete) => (
                <Card 
                  key={athlete.id} 
                  className="overflow-hidden hover:shadow-lg hover:scale-[1.02] transition-all duration-200 cursor-pointer"
                  onClick={() => handleAthleteClick(athlete)}
                >
                  <CardContent className="p-0">
                    <div className="relative">
                      <Avatar className="w-full h-48 rounded-none">
                        <SafeAvatarImage src={athlete.headshot_url || ''} className="object-cover" />
                        <AvatarFallback className="rounded-none text-2xl text-white font-bold bg-muted">
                          {athlete.first_name?.[0] || athlete.name[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="absolute bottom-2 left-2">
                        <span className="bg-background/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold">
                          {athlete.display_name || athlete.name}
                        </span>
                      </div>
                      <div className="absolute bottom-2 right-2 flex items-center gap-1">
                        {getLeagueLogo(athlete.league, athlete.sport) && (
                          <div className="h-7 w-7 rounded-full flex items-center justify-center shadow-md" style={{ backgroundColor: getLeagueColor(athlete.league, athlete.sport) }}>
                            <img src={getLeagueLogo(athlete.league, athlete.sport)!} alt="" className="h-5 w-5 object-contain" />
                          </div>
                        )}
                        {athlete.current_team_logo && (
                          <div className="h-7 w-7 rounded-full flex items-center justify-center shadow-md" style={{ backgroundColor: getTeamColors(athlete.current_team_name || '').primary }}>
                            <img src={athlete.current_team_logo} alt="" className="h-5 w-5 object-contain" />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-y-3 text-sm">
                        <div>
                          <p className="text-xs text-muted-foreground uppercase">Team</p>
                          <p className="font-bold">{athlete.current_team_name || '—'}{athlete.jersey_number ? ` #${athlete.jersey_number}` : ''}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase">Position</p>
                          <p className="font-bold">{athlete.position || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase">Age</p>
                          <p className="font-bold">{athlete.age || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase">Height</p>
                          <p className="font-bold">{athlete.height || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase">Weight</p>
                          <p className="font-bold">{athlete.weight || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase">Experience</p>
                          <p className="font-bold">{athlete.years_experience ? `${athlete.years_experience} yr${athlete.years_experience !== 1 ? 's' : ''}` : '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase">Draft</p>
                          <p className="font-bold">{athlete.draft_year ? `${athlete.draft_year}${athlete.draft_round ? ` R${athlete.draft_round}` : ''}${athlete.draft_number ? ` P${athlete.draft_number}` : ''}` : '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase">From</p>
                          <p className="font-bold">{[athlete.birthplace_city, athlete.birthplace_state].filter(Boolean).join(', ') || '—'}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            )
          ) : (
            <EmptyState
              title="No Athletes Found"
              description={marketplaceSearchTerm || marketplaceFilterBySport !== 'all' 
                ? "Try adjusting your search or filter criteria."
                : "No athletes synced yet. Contact admin to sync athlete data from the admin panel."
              }
              icon={<Trophy className="w-8 h-8 text-muted-foreground" />}
            />
          )}

          {/* Pagination for Professional Athletes */}
          {(() => {
            const total = marketplaceAthletesData?.total || 0;
            const totalPages = Math.ceil(total / PAGE_SIZE);
            if (totalPages <= 1) return null;
            const start = (marketplacePage - 1) * PAGE_SIZE + 1;
            const end = Math.min(marketplacePage * PAGE_SIZE, total);
            
            const getPageNumbers = () => {
              const pages: (number | 'ellipsis')[] = [];
              if (totalPages <= 7) {
                for (let i = 1; i <= totalPages; i++) pages.push(i);
              } else {
                pages.push(1);
                if (marketplacePage > 3) pages.push('ellipsis');
                for (let i = Math.max(2, marketplacePage - 1); i <= Math.min(totalPages - 1, marketplacePage + 1); i++) pages.push(i);
                if (marketplacePage < totalPages - 2) pages.push('ellipsis');
                pages.push(totalPages);
              }
              return pages;
            };
            
            return (
              <div className="flex flex-col items-center gap-3 mt-6">
                <p className="text-sm text-muted-foreground">Showing {start}-{end} of {total.toLocaleString()} athletes</p>
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={() => { if (marketplacePage > 1) setMarketplacePage(p => p - 1); }}
                        className={marketplacePage <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>
                    {getPageNumbers().map((page, i) => (
                      <PaginationItem key={i}>
                        {page === 'ellipsis' ? (
                          <PaginationEllipsis />
                        ) : (
                          <PaginationLink
                            isActive={page === marketplacePage}
                            onClick={() => setMarketplacePage(page)}
                            className="cursor-pointer"
                          >
                            {page}
                          </PaginationLink>
                        )}
                      </PaginationItem>
                    ))}
                    <PaginationItem>
                      <PaginationNext 
                        onClick={() => { if (marketplacePage < totalPages) setMarketplacePage(p => p + 1); }}
                        className={marketplacePage >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            );
          })()}
        </TabsContent>

        {/* Collegiate Athletes Tab */}
        <TabsContent value="collegiate" className="space-y-6">

          {/* Collegiate Filters and Search */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search collegiate athletes by name..."
                value={collegiateSearchTerm}
                onChange={(e) => setCollegiateSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={collegiateFilterBySport} onValueChange={handleCollegiateSportChange}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Sports" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sports</SelectItem>
                {collegiateSports.map(sport => (
                  <SelectItem key={sport} value={sport}>
                    {collegiateSportsDisplay[sport] || sport}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={collegiateFilterByTeam} onValueChange={setCollegiateFilterByTeam}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Teams" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Teams</SelectItem>
                {collegiateTeams.map(team => (
                  <SelectItem key={team} value={team}>
                    {team}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={collegiateFilterByClaimStatus} onValueChange={setCollegiateFilterByClaimStatus}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="approved">Claimed</SelectItem>
                <SelectItem value="unclaimed">Unclaimed</SelectItem>
              </SelectContent>
            </Select>
            <ViewToggle view={collegiateView} onViewChange={setCollegiateView} />
          </div>

          {/* Collegiate Athletes Grid */}
          {collegiateLoading ? (
            <LoadingFallback />
          ) : filteredCollegiateAthletes.length > 0 ? (
            collegiateView === 'list' ? (
              <AthleteListView athletes={filteredCollegiateAthletes} onAthleteClick={handleCollegiateAthleteClick} isCollegiate collegiateSportsDisplay={collegiateSportsDisplay} />
            ) : collegiateView === 'compact' ? (
              <AthleteCompactView athletes={filteredCollegiateAthletes} onAthleteClick={handleCollegiateAthleteClick} isCollegiate collegiateSportsDisplay={collegiateSportsDisplay} />
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredCollegiateAthletes.map((athlete: ESPNAthlete) => (
                <Card 
                  key={athlete.id} 
                  className="overflow-hidden hover:shadow-lg hover:scale-[1.02] transition-all duration-200 cursor-pointer"
                  onClick={() => handleCollegiateAthleteClick(athlete)}
                >
                  <CardContent className="p-0">
                    <div className="relative">
                      <Avatar className="w-full h-48 rounded-none">
                        <SafeAvatarImage src={athlete.headshot_url || ''} className="object-cover" />
                        <AvatarFallback className="rounded-none text-2xl text-white font-bold bg-muted">
                          {athlete.first_name?.[0] || athlete.name[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="absolute bottom-2 left-2">
                        <span className="bg-background/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold">
                          {athlete.display_name || athlete.name}
                        </span>
                      </div>
                      <div className="absolute bottom-2 right-2 flex items-center -space-x-1">
                        {athlete.current_team_logo && (
                          <div className="h-7 w-7 rounded-full flex items-center justify-center border-2 border-background shadow-md" style={{ backgroundColor: getTeamColors(athlete.current_team_name || '', 'college').primary }}>
                            <img src={athlete.current_team_logo} alt="" className="h-5 w-5 object-contain" />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-y-3 text-sm">
                        <div>
                          <p className="text-xs text-muted-foreground uppercase">Team</p>
                          <p className="font-bold">{athlete.current_team_name || '—'}{athlete.jersey_number ? ` #${athlete.jersey_number}` : ''}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase">Position</p>
                          <p className="font-bold">{athlete.position || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase">Age</p>
                          <p className="font-bold">{athlete.age || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase">Height</p>
                          <p className="font-bold">{athlete.height || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase">Weight</p>
                          <p className="font-bold">{athlete.weight || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase">Experience</p>
                          <p className="font-bold">{athlete.years_experience ? `${athlete.years_experience} yr${athlete.years_experience !== 1 ? 's' : ''}` : '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase">School</p>
                          <p className="font-bold">{athlete.college || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground uppercase">From</p>
                          <p className="font-bold">{[athlete.birthplace_city, athlete.birthplace_state].filter(Boolean).join(', ') || '—'}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            )
          ) : (
            <EmptyState
              title="No Collegiate Athletes Found"
              description={collegiateSearchTerm || collegiateFilterBySport !== 'all' 
                ? "Try adjusting your search or filter criteria."
                : "No collegiate athletes synced yet. Contact admin to sync NCAA athlete data from the admin panel."
              }
              icon={<GraduationCap className="w-8 h-8 text-muted-foreground" />}
            />
          )}

          {/* Pagination for Collegiate Athletes */}
          {(() => {
            const total = collegiateAthletesData?.total || 0;
            const totalPages = Math.ceil(total / PAGE_SIZE);
            if (totalPages <= 1) return null;
            const start = (collegiatePage - 1) * PAGE_SIZE + 1;
            const end = Math.min(collegiatePage * PAGE_SIZE, total);
            
            const getPageNumbers = () => {
              const pages: (number | 'ellipsis')[] = [];
              if (totalPages <= 7) {
                for (let i = 1; i <= totalPages; i++) pages.push(i);
              } else {
                pages.push(1);
                if (collegiatePage > 3) pages.push('ellipsis');
                for (let i = Math.max(2, collegiatePage - 1); i <= Math.min(totalPages - 1, collegiatePage + 1); i++) pages.push(i);
                if (collegiatePage < totalPages - 2) pages.push('ellipsis');
                pages.push(totalPages);
              }
              return pages;
            };
            
            return (
              <div className="flex flex-col items-center gap-3 mt-6">
                <p className="text-sm text-muted-foreground">Showing {start}-{end} of {total.toLocaleString()} athletes</p>
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={() => { if (collegiatePage > 1) setCollegiatePage(p => p - 1); }}
                        className={collegiatePage <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>
                    {getPageNumbers().map((page, i) => (
                      <PaginationItem key={i}>
                        {page === 'ellipsis' ? (
                          <PaginationEllipsis />
                        ) : (
                          <PaginationLink
                            isActive={page === collegiatePage}
                            onClick={() => setCollegiatePage(page)}
                            className="cursor-pointer"
                          >
                            {page}
                          </PaginationLink>
                        )}
                      </PaginationItem>
                    ))}
                    <PaginationItem>
                      <PaginationNext 
                        onClick={() => { if (collegiatePage < totalPages) setCollegiatePage(p => p + 1); }}
                        className={collegiatePage >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            );
          })()}
        </TabsContent>

        {/* Creators Tab */}
        <TabsContent value="creators" className="space-y-6">
          {/* Creators Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Creators Found</CardTitle>
                <Video className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {creatorsSearchMode === 'synced' ? displayedCreatorGroups.length : displayedCreators.length}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {creatorsSearchMode === 'synced' ? 'Unified profiles' : 'Search results'}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Verified Creators</CardTitle>
                <CheckCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {creatorsSearchMode === 'synced' 
                    ? displayedCreatorGroups.filter(g => g.is_verified).length
                    : displayedCreators.filter(c => c.is_verified).length
                  }
                </div>
                <p className="text-xs text-muted-foreground mt-1">Verified profiles</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Followers</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {(() => {
                    const total = creatorsSearchMode === 'synced'
                      ? displayedCreatorGroups.reduce((sum, g) => sum + g.total_followers, 0)
                      : displayedCreators.reduce((sum, c) => sum + c.follower_count, 0);
                    if (total >= 1000000000) return `${(total / 1000000000).toFixed(1)}B`;
                    if (total >= 1000000) return `${(total / 1000000).toFixed(1)}M`;
                    if (total >= 1000) return `${(total / 1000).toFixed(0)}K`;
                    return total;
                  })()}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Combined reach</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Multi-Platform</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {displayedCreatorGroups.filter(g => g.platforms.length > 1).length}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Unified profiles
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Creators Filters and Search */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search creators by name or username..."
                value={creatorsSearchTerm}
                onChange={(e) => setCreatorsSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={creatorsPlatform} onValueChange={(value: 'tiktok' | 'instagram' | 'youtube' | 'all') => setCreatorsPlatform(value)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Platform" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Platforms</SelectItem>
                <SelectItem value="tiktok">TikTok</SelectItem>
                <SelectItem value="instagram">Instagram</SelectItem>
                <SelectItem value="youtube">YouTube</SelectItem>
              </SelectContent>
            </Select>
            <Select value={creatorsSearchMode} onValueChange={(value: 'synced' | 'live') => setCreatorsSearchMode(value)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Search Mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="synced">Synced Creators</SelectItem>
                <SelectItem value="live">Live API Search</SelectItem>
              </SelectContent>
            </Select>
            <ViewToggle view={creatorsView} onViewChange={setCreatorsView} />
          </div>

          {/* Creators Grid */}
          {creatorsLoading ? (
            <LoadingFallback />
          ) : creatorsSearchMode === 'synced' && displayedCreatorGroups.length > 0 ? (
            creatorsView === 'list' ? (
              <CreatorGroupListView groups={displayedCreatorGroups} onGroupClick={handleCreatorGroupClick} />
            ) : creatorsView === 'compact' ? (
              <CreatorGroupCompactView groups={displayedCreatorGroups} onGroupClick={handleCreatorGroupClick} />
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayedCreatorGroups.map((group) => {
                const isMultiPlatform = group.platforms.length > 1;
                const primaryCreator = group.creators[0];
                
                return (
                  <Card 
                    key={group.id} 
                    className="overflow-hidden hover:shadow-lg hover:scale-[1.02] transition-all duration-200 cursor-pointer shadow-slice bg-card"
                    onClick={() => handleCreatorGroupClick(group)}
                  >
                    <CardContent className="p-0">
                      <div className="relative">
                        <Avatar className="w-full h-48 rounded-none">
                          <SafeAvatarImage src={getProxiedImageUrl(group.avatar_url)} className="object-cover" />
                          <AvatarFallback className="rounded-none text-2xl bg-gradient-to-br from-muted to-muted/60">
                            {(group.display_name || group.primary_name || 'U')[0]}
                          </AvatarFallback>
                        </Avatar>
                        {/* Name pill badge - bottom left */}
                        <div className="absolute bottom-2 left-2 bg-background/95 backdrop-blur-sm rounded-full px-3 py-1 shadow-sm">
                          <span className="text-xs font-semibold text-foreground truncate max-w-[140px] block">
                            {group.display_name || group.primary_name}
                          </span>
                        </div>
                        {/* Platform icon badge - bottom right */}
                        <div className="absolute bottom-2 right-2 flex gap-1.5">
                          {group.platforms.map(platform => (
                            <div 
                              key={platform}
                              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shadow-md ${
                                platform === 'tiktok' ? 'bg-transparent p-0 overflow-hidden' :
                                platform === 'instagram' ? 'bg-gradient-to-br from-[#833ab4] via-[#fd1d1d] to-[#fcb045] text-white' :
                                'bg-[#ff0000] text-white'
                              }`}
                            >
                              {getPlatformIcon(platform, platform === 'tiktok' ? 'w-full h-full' : 'h-4 w-4')}
                            </div>
                          ))}
                        </div>
                      </div>
                      {/* 2x2 Stats Grid */}
                      <div className="p-4">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Username</p>
                            <p className="text-sm font-semibold text-foreground truncate">
                              @{primaryCreator?.username || group.primary_name}
                            </p>
                          </div>
                          <div>
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Followers</p>
                            <p className="text-sm font-semibold text-foreground">
                              {group.total_followers >= 1000000000 
                                ? `${(group.total_followers / 1000000000).toFixed(1)}B`
                                : group.total_followers >= 1000000 
                                  ? `${(group.total_followers / 1000000).toFixed(1)}M`
                                  : group.total_followers >= 1000 
                                    ? `${(group.total_followers / 1000).toFixed(0)}K`
                                    : group.total_followers
                              }
                            </p>
                          </div>
                          <div>
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Posts</p>
                            <p className="text-sm font-semibold text-foreground">
                              {group.creators.reduce((sum, c) => sum + (c.video_count ?? 0), 0)}
                            </p>
                          </div>
                          <div>
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Likes</p>
                            <p className="text-sm font-semibold text-foreground">
                              {(() => {
                                const likes = group.creators.reduce((sum, c) => sum + (c.like_count ?? 0), 0);
                                if (likes >= 1000000000) return `${(likes / 1000000000).toFixed(1)}B`;
                                if (likes >= 1000000) return `${(likes / 1000000).toFixed(1)}M`;
                                if (likes >= 1000) return `${(likes / 1000).toFixed(0)}K`;
                                return likes;
                              })()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            )
          ) : creatorsSearchMode === 'live' && displayedCreators.length > 0 ? (
            creatorsView === 'list' ? (
              <CreatorListView creators={displayedCreators} onCreatorClick={handleCreatorClick} />
            ) : creatorsView === 'compact' ? (
              <CreatorCompactView creators={displayedCreators} onCreatorClick={handleCreatorClick} />
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayedCreators.map((creator: Creator) => (
                <Card 
                  key={creator.id} 
                  className="overflow-hidden hover:shadow-lg hover:scale-[1.02] transition-all duration-200 cursor-pointer shadow-slice bg-card"
                  onClick={() => handleCreatorClick(creator)}
                >
                  <CardContent className="p-0">
                    <div className="relative">
                      <Avatar className="w-full h-48 rounded-none">
                        <SafeAvatarImage src={getProxiedImageUrl(creator.avatar_url)} className="object-cover" />
                        <AvatarFallback className="rounded-none text-2xl bg-gradient-to-br from-muted to-muted/60">
                          {creator.display_name[0] || creator.username[0]}
                        </AvatarFallback>
                      </Avatar>
                      {/* Name pill badge - bottom left */}
                      <div className="absolute bottom-2 left-2 bg-background/95 backdrop-blur-sm rounded-full px-3 py-1 shadow-sm">
                        <span className="text-xs font-semibold text-foreground truncate max-w-[140px] block">
                          {creator.display_name}
                        </span>
                      </div>
                      {/* Platform icon badge - bottom right */}
                      <div className={`absolute bottom-2 right-2 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shadow-md ${
                        creator.platform === 'tiktok' ? 'bg-transparent p-0 overflow-hidden' :
                        creator.platform === 'instagram' ? 'bg-gradient-to-br from-[#833ab4] via-[#fd1d1d] to-[#fcb045] text-white' :
                        'bg-[#ff0000] text-white'
                      }`}>
                        {getPlatformIcon(creator.platform, creator.platform === 'tiktok' ? 'w-full h-full' : 'h-4 w-4')}
                      </div>
                    </div>
                    {/* 2x2 Stats Grid */}
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Username</p>
                          <p className="text-sm font-semibold text-foreground truncate">@{creator.username}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Followers</p>
                          <p className="text-sm font-semibold text-foreground">
                            {creator.follower_count >= 1000000 
                              ? `${(creator.follower_count / 1000000).toFixed(1)}M`
                              : creator.follower_count >= 1000 
                                ? `${(creator.follower_count / 1000).toFixed(0)}K`
                                : creator.follower_count
                            }
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                            {creator.platform === 'youtube' ? 'Videos' : 'Posts'}
                          </p>
                          <p className="text-sm font-semibold text-foreground">{creator.video_count}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Likes</p>
                          <p className="text-sm font-semibold text-foreground">
                            {creator.like_count >= 1000000 
                              ? `${(creator.like_count / 1000000).toFixed(1)}M`
                              : creator.like_count >= 1000 
                                ? `${(creator.like_count / 1000).toFixed(0)}K`
                                : creator.like_count
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            )
          ) : (
            <EmptyState
              title={creatorsSearchMode === 'synced' 
                ? (creatorsSearchTerm ? "No Creators Found" : "No Creators")
                : (creatorsSearchTerm ? "No Creators Found" : "Search for Creators")
              }
              description={creatorsSearchMode === 'synced'
                ? (creatorsSearchTerm 
                    ? "Try adjusting your search term or switching to Live API Search."
                    : "No creators have been synced yet. Switch to Live API Search to find and sync new creators.")
                : (creatorsSearchTerm 
                    ? "Try adjusting your search term or changing the platform."
                    : "Enter a name or username to search for creators across TikTok, Instagram, and YouTube.")
              }
              icon={<Video className="w-8 h-8 text-muted-foreground" />}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Athlete Detail Modal */}
      <MarketplaceAthleteDetailModal
        athlete={selectedAthlete}
        open={isDetailModalOpen}
        onOpenChange={setIsDetailModalOpen}
        onInviteToDeal={handleInviteAthleteToDeal}
      />
      
      {/* Collegiate Athlete Detail Modal */}
      <MarketplaceAthleteDetailModal
        athlete={selectedCollegiateAthlete}
        open={isCollegiateDetailModalOpen}
        onOpenChange={setIsCollegiateDetailModalOpen}
        onInviteToDeal={handleInviteAthleteToDeal}
      />
      
      {/* Creator Detail Modal */}
      <CreatorDetailModal
        creator={selectedCreator}
        open={isCreatorModalOpen}
        onOpenChange={setIsCreatorModalOpen}
        onInviteToDeal={handleInviteCreatorToDeal}
      />
      
      {/* Grouped Creator Detail Modal */}
      <GroupedCreatorDetailModal
        creatorGroup={selectedCreatorGroup}
        open={isGroupedCreatorModalOpen}
        onOpenChange={setIsGroupedCreatorModalOpen}
        onInviteToDeal={handleInviteCreatorGroupToDeal}
      />
    </div>
  );
};

export default EnhancedTalentMarketplace;
